import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Input, Alert, List, Typography, Space, Radio, Select, Spin, Empty, Tag, Divider, Tooltip, Button, Table } from "antd";
import { EnvironmentOutlined, InfoCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, SearchOutlined, RocketOutlined, CopyOutlined } from "@ant-design/icons";
// import uldApi from "../../../../api/uldApi"; // 已改为使用enhancedApi
import { useUldAirports } from "../../hooks/useUldAirports";
import { ULD_LOCATION_TYPE, ULD_LOCATION_TYPE_NAMES } from "../constants/uldConstants";
import { ULD_CORE_STATUS_NAMES, ULD_CORE_STATUS_COLORS, getLocationUpdateBlockReason } from "../constants/uldConstantsV3";
import { FLIGHT_STATUS_LABELS, FLIGHT_STATUS_COLORS } from "../../../flightOperation/Monitor/constants/flightConstants";
import dayjs from "dayjs";
import { uldService, flightOperationFlightService } from "../../../../api/enhancedApi";
import { message } from "antd";
import { airportApi } from "../../../../api/masterDataApi";

const { Text, Paragraph } = Typography;
const { Option } = Select;

/**
 * 批量更新ULD位置的弹窗组件
 */
const LocationBatchUpdateModal = ({ visible, onCancel, onSuccess, selectedUlds = [] }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [flightOptions, setFlightOptions] = useState([]);
    const [flightInputMode, setFlightInputMode] = useState("existing"); // "existing" | "manual"
    const [flightSearchKeyword, setFlightSearchKeyword] = useState('');
    
    // 【新增】前置检查状态
    const [preCheckResult, setPreCheckResult] = useState({ eligible: [], ineligible: [] });
    const [showPreCheck, setShowPreCheck] = useState(false);
    
    const { airports, airportsList, loading: airportsLoading } = useUldAirports();

    // 位置类型选项
    const locationTypeOptions = [
        { label: "机场", value: "AIRPORT" },
        { label: "航班", value: "FLIGHT" },
        { label: "其他位置", value: "OTHER_LOCATION" },
    ];

    // 监听位置类型变化
    const locationType = Form.useWatch('location_type', form);

    // 【新增】位置一致性校验错误状态
    const [consistencyErrors, setConsistencyErrors] = useState([]);
    const [forceUpdate, setForceUpdate] = useState(false);

    // 加载机场选项
    const [airportOptions, setAirportOptions] = useState([]);

    // 【修复】搜索航班 - 参考单个ULD更新的实现
    const searchFlights = useCallback(async (keyword = '') => {
        if (!keyword || keyword.length < 2) {
            setFlightOptions([]);
            return;
        }
        
        setSearchLoading(true);
        try {
            // 获取航班数据，使用较大的分页以获取更多选项
            const params = {
                page: 1,
                page_size: 50,
                // 设置时间范围：过去1天到未来7天
                start_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                end_date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                // 使用关键词搜索（后端支持航班号搜索）
                search_text: keyword,
                // 只获取可能的航班状态
                status: 'SCHEDULED,DEPARTED,DELAYED'
            };

            console.log('搜索航班参数:', params);
            const response = await flightOperationFlightService.getFlightOperations(params);
            
            if (response.data && response.data.items) {
                setFlightOptions(response.data.items);
            } else {
                setFlightOptions([]);
            }
        } catch (error) {
            console.error('搜索航班失败:', error);
            setFlightOptions([]);
        } finally {
            setSearchLoading(false);
        }
    }, []);

    // 处理航班搜索输入
    const handleFlightSearch = useCallback((value) => {
        setFlightSearchKeyword(value);
        searchFlights(value);
    }, [searchFlights]);

    // 【新增】渲染航班选择器选项 - 参考单个ULD更新的实现
    const renderFlightOption = (flight) => {
        const flightNumber = flight.flight_number || `ID-${flight.id}`;
        const route = `${flight.departure_airport} → ${flight.arrival_airport}`;
        const aircraftReg = flight.aircraft_reg || '未分配';
        const status = flight.status;
        const statusColor = FLIGHT_STATUS_COLORS[status] || 'default';
        const statusLabel = FLIGHT_STATUS_LABELS[status] || status;
        
        // 显示计划时间或实际时间
        const departureTime = flight.etd || flight.std;
        const timeDisplay = departureTime ? dayjs(departureTime).format('MM-DD HH:mm') : '';

        return (
            <div style={{ 
                padding: '8px 12px', 
                lineHeight: '1.4',
                minHeight: '48px'
            }}>
                {/* 第一行：航班号、状态和时间 */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '4px'
                }}>
                    <Space size="small">
                        <Tag icon={<RocketOutlined />} color="blue" style={{ margin: 0 }}>
                            {flightNumber}
                        </Tag>
                        <Tag color={statusColor} style={{ margin: 0 }}>
                            {statusLabel}
                        </Tag>
                    </Space>
                    {timeDisplay && (
                        <span style={{ 
                            fontSize: '12px', 
                            color: '#999',
                            whiteSpace: 'nowrap'
                        }}>
                            {timeDisplay}
                        </span>
                    )}
                </div>
                
                {/* 第二行：航线和机型 */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <span style={{ 
                        fontWeight: 500, 
                        color: '#262626',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px'
                    }}>
                        {route}
                    </span>
                    <span style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        marginLeft: '8px',
                        whiteSpace: 'nowrap'
                    }}>
                        {aircraftReg}
                    </span>
                </div>
            </div>
        );
    };

    // 用稳定 key，避免父组件每次 render 传入新数组时清掉一致性警告（导致强制更新 UI 闪一下就没）
    const selectedUldKey = selectedUlds
        .map((uld) => uld?.master?.id ?? uld?.master?.uld_identifier)
        .filter(Boolean)
        .join(',');

    // 【新增】前置检查逻辑：仅在弹窗打开或选中集合真正变化时重置
    useEffect(() => {
        if (visible && selectedUlds.length > 0) {
            setConsistencyErrors([]);
            setForceUpdate(false);
            setFlightOptions([]);
            setFlightSearchKeyword('');
            
            performPreCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedUldKey 代表选中集合身份
    }, [visible, selectedUldKey]);

    const performPreCheck = () => {
        const eligible = [];
        const ineligible = [];
        
        selectedUlds.forEach(uld => {
            const coreStatus = uld.stock?.core_status || 'IN_STOCK';
            const identifier = uld.master.uld_identifier;
            const isDelivered = uld.master?.delivered_to_consignee;
            const consigneeName = uld.master?.consignee_name;
            
            const blockReason = getLocationUpdateBlockReason(coreStatus, isDelivered, consigneeName);
            if (blockReason) {
                ineligible.push({
                    identifier,
                    coreStatus,
                    reason: blockReason,
                });
                return;
            }
            eligible.push({ identifier, coreStatus });
        });
        
        setPreCheckResult({ eligible, ineligible });
        setShowPreCheck(ineligible.length > 0);
    };

    // 处理提交
    const handleSubmit = async () => {
        let shouldSetLoadingFalse = true;
        
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 【更新】只处理符合条件的ULD
            const eligibleIdentifiers = preCheckResult.eligible.map(item => item.identifier);
            
            const requestData = {
                uld_identifiers: eligibleIdentifiers,
                location_type: values.location_type,
                airport_iata: values.airport_iata,
                flight_op_id: values.flight_op_id,
                transport_identifier: values.transport_identifier,
                new_other_location: values.new_other_location,
                // 【新增】添加强制更新标志和原因
                force_update: forceUpdate,
                reason: values.reason
            };

            console.log('批量更新ULD位置:', { forceUpdate, requestData });
            
            const response = await uldService.batchUpdateLocation(requestData);

            // 按照API_GUIDELINES规范处理响应格式
            const apiResult = response.data;
            const result = apiResult.data;
            const { success_count, failure_count, failure_items } = result;
            
            if (success_count > 0) {
                if (failure_count > 0) {
                    // 部分成功的情况
                    const failureList = (failure_items || []).map(item => `${item.identifier}: ${item.error}`).join('\n');
                    Modal.info({
                        title: "批量位置更新完成",
                        content: (
                            <div>
                                <p>✅ 成功更新 <strong>{success_count}</strong> 个ULD</p>
                                {preCheckResult.ineligible.length > 0 && (
                                    <p>⚠️ 跳过 <strong>{preCheckResult.ineligible.length}</strong> 个状态不符合的ULD</p>
                                )}
                                {failure_count > 0 && (
                                    <div>
                                        <p>❌ 失败 <strong>{failure_count}</strong> 个ULD：</p>
                                        <pre style={{ fontSize: '12px', maxHeight: '150px', overflow: 'auto' }}>
                                            {failureList}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ),
                        width: 600
                    });
                } else {
                    // 全部成功
                    Modal.success({
                        title: "批量位置更新成功",
                        content: `✅ 成功更新 ${success_count} 个ULD的位置信息`
                    });
                }
                onSuccess?.();
            } else {
                // 全部失败
                Modal.error({
                    title: "批量位置更新失败", 
                    content: "所有ULD都无法更新，请检查状态和权限"
                });
            }

        } catch (error) {
            if (error.errorFields) {
                // 表单验证错误
                message.error("请检查表单填写是否正确");
                return;
            }
            
            console.error("批量位置更新失败:", error);
            
            // 【新增】处理位置一致性校验错误 - 但不在强制更新时处理
            if (error.response && error.response.data && !forceUpdate) {
                const errorData = error.response.data;
                
                // 检查是否为批量位置一致性错误
                if (errorData.detail && errorData.detail.includes('位置一致性检查失败')) {
                    try {
                        // 后端应该在error_details中返回所有一致性错误的详情
                        if (errorData.error_details && Array.isArray(errorData.error_details)) {
                            // 【修复】将后端返回的details数组转换为前端期望的格式
                            const formattedErrors = errorData.error_details.map(details => ({
                                details: details
                            }));
                            setConsistencyErrors(formattedErrors);
                            message.warning(`检测到 ${errorData.error_details.length} 个ULD存在位置一致性问题，请查看警告信息`);
                            shouldSetLoadingFalse = false;
                            setLoading(false);
                            return;
                        }
                        
                        // 如果没有详细的错误列表，尝试解析错误消息
                        const errorMessage = errorData.detail;
                        if (errorMessage.includes('一致性')) {
                            // 创建一个通用的一致性错误提示
                            setConsistencyErrors([{
                                details: {
                                    uld_identifier: "批量ULD",
                                    check_type: "批量一致性检查",
                                    consistency_error: true,
                                    error_message: errorMessage
                                }
                            }]);
                            message.warning('检测到位置一致性问题，请查看警告信息');
                            shouldSetLoadingFalse = false;
                            setLoading(false);
                            return;
                        }
                    } catch (e) {
                        console.error('解析批量位置一致性错误失败:', e);
                    }
                }
            }
            
            const errorMessage = error.response?.data?.detail || "批量位置更新失败，请重试";
            Modal.error({
                title: "操作失败",
                content: errorMessage
            });
        } finally {
            if (shouldSetLoadingFalse) {
                setLoading(false);
            }
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        setFlightOptions([]);
        setFlightInputMode("existing");
        setFlightSearchKeyword('');
        onCancel && onCancel();
    };

    // 重置表单时清空航班选项
    useEffect(() => {
        if (!visible) {
            setFlightOptions([]);
            setFlightInputMode("existing");
            setFlightSearchKeyword('');
        }
    }, [visible]);

    // 加载机场选项
    useEffect(() => {
        const fetchAirports = async () => {
            try {
                // 使用airportApi获取机场列表
                const response = await airportApi.getAirportList({ page_size: 500 });
                const airports = response.data?.items || [];
                const options = airports.map(airport => ({
                    label: `${airport.iata_code} - ${airport.name}`,
                    value: airport.iata_code
                }));
                setAirportOptions(options);
            } catch (error) {
                console.error('获取机场选项失败:', error);
                message.error('获取机场选项失败');
            }
        };
        fetchAirports();
    }, []);

    // 表单值变化时重置位置一致性错误（优化为更精确的依赖）
    const handleFormFieldChange = useCallback(() => {
        if (consistencyErrors.length > 0) {
            setConsistencyErrors([]);
            setForceUpdate(false);
        }
    }, [consistencyErrors.length]);

    // 处理位置类型变化
    const handleLocationTypeChange = (e) => {
        const newLocationType = e.target.value;
        form.setFieldsValue({ location_type: newLocationType });
        
        // 清除其他字段
        if (newLocationType === ULD_LOCATION_TYPE.AIRPORT) {
            form.setFieldsValue({ 
                flight_op_id: undefined,
                transport_identifier: undefined,
                new_other_location: undefined
            });
        } else if (newLocationType === ULD_LOCATION_TYPE.FLIGHT) {
            form.setFieldsValue({ 
                airport_iata: undefined,
                new_other_location: undefined
            });
        } else if (newLocationType === ULD_LOCATION_TYPE.OTHER_LOCATION) {
            form.setFieldsValue({ 
                airport_iata: undefined,
                flight_op_id: undefined,
                transport_identifier: undefined
            });
        }
        
        // 重置一致性错误
        handleFormFieldChange();
    };

    // 【新增】复制ULD列表到剪贴板
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(
            () => {
                message.success('已复制到剪贴板');
            },
            () => {
                message.error('复制失败');
            }
        );
    };

    // 【新增】强制更新位置
    const handleForceUpdate = async () => {
        let shouldSetLoadingFalse = true;
        
        try {
            // 验证表单，特别是原因字段
            const values = await form.validateFields();
            
            // 确保填写了原因
            if (!values.reason || values.reason.trim() === '') {
                message.error('强制更新时必须填写变更原因');
                return;
            }
            
            setLoading(true);
            
            // 【更新】只处理符合条件的ULD
            const eligibleIdentifiers = preCheckResult.eligible.map(item => item.identifier);
            
            // 构建请求数据，强制设置force_update为true
            const requestData = {
                uld_identifiers: eligibleIdentifiers,
                location_type: values.location_type,
                airport_iata: values.airport_iata,
                flight_op_id: values.flight_op_id,
                transport_identifier: values.transport_identifier,
                new_other_location: values.new_other_location,
                force_update: true, // 直接设置为true，不依赖状态
                reason: values.reason
            };

            console.log('强制批量更新ULD位置:', { force_update: true, requestData });

            const response = await uldService.batchUpdateLocation(requestData);

            // 按照API_GUIDELINES规范处理响应格式
            const apiResult = response.data;
            const result = apiResult.data;
            const { success_count, failure_count, failure_items } = result;
            
            if (success_count > 0) {
                if (failure_count > 0) {
                    // 部分成功的情况
                    const failureList = (failure_items || []).map(item => `${item.identifier}: ${item.error}`).join('\n');
                    Modal.info({
                        title: "批量位置强制更新完成",
                        content: (
                            <div>
                                <p>✅ 成功强制更新 <strong>{success_count}</strong> 个ULD</p>
                                {preCheckResult.ineligible.length > 0 && (
                                    <p>⚠️ 跳过 <strong>{preCheckResult.ineligible.length}</strong> 个状态不符合的ULD</p>
                                )}
                                {failure_count > 0 && (
                                    <div>
                                        <p>❌ 失败 <strong>{failure_count}</strong> 个ULD：</p>
                                        <pre style={{ fontSize: '12px', maxHeight: '150px', overflow: 'auto' }}>
                                            {failureList}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ),
                        width: 600
                    });
                } else {
                    // 全部成功
                    Modal.success({
                        title: "批量位置强制更新成功",
                        content: `✅ 成功强制更新 ${success_count} 个ULD的位置信息`
                    });
                }
                
                // 【修复】强制更新成功后清理状态
                setConsistencyErrors([]);
                setForceUpdate(false);
                
                onSuccess?.();
                onCancel();
            } else {
                // 全部失败
                Modal.error({
                    title: "批量位置强制更新失败", 
                    content: "所有ULD都无法更新，请检查状态和权限"
                });
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写是否正确');
                return;
            }
            
            console.error('强制批量更新失败:', error);
            const errorMessage = error.response?.data?.detail || 
                               error.response?.data?.message || 
                               "强制批量更新失败";
            
            // 强制更新时不解析一致性错误，直接显示错误信息
            Modal.error({
                title: "强制更新失败",
                content: errorMessage
            });
        } finally {
            if (shouldSetLoadingFalse) {
                setLoading(false);
            }
        }
    };

    // 根据位置类型渲染相应的表单字段
    const renderLocationFields = () => {
        switch (locationType) {
            case "AIRPORT":
                return (
                    <Form.Item
                        name="airport_iata"
                        label="目标机场"
                        rules={[{ required: true, message: "请选择机场" }]}
                    >
                        <Select
                            placeholder="请选择机场"
                            showSearch
                            onChange={handleFormFieldChange}
                            filterOption={(input, option) => {
                                const airport = (airportsList || []).find(a => a.iata_code === option.value);
                                if (!airport) return false;
                                const searchText = `${airport.iata_code} ${airport.name_cn || ''} ${airport.name_en || ''}`.toLowerCase();
                                return searchText.indexOf(input.toLowerCase()) >= 0;
                            }}
                        >
                            {(airportsList || []).map(airport => (
                                <Option key={airport.iata_code} value={airport.iata_code}>
                                    {airport.iata_code}{(airport.name_cn || airport.name_en) ? ` - ${airport.name_cn || airport.name_en}` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                );

            case "FLIGHT":
                return (
                    <>
                        <Form.Item label="航班选择方式">
                            <Radio.Group
                                value={flightInputMode}
                                onChange={(e) => {
                                    setFlightInputMode(e.target.value);
                                    // 清空表单中的航班相关字段
                                    form.setFieldsValue({
                                        flight_op_id: undefined,
                                        transport_identifier: undefined
                                    });
                                    // 清空搜索结果
                                    setFlightOptions([]);
                                    setFlightSearchKeyword('');
                                    // 重置一致性错误
                                    handleFormFieldChange();
                                }}
                            >
                                <Radio value="existing">选择现有航班</Radio>
                                <Radio value="manual">手动填写航班号</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {flightInputMode === "existing" && (
                            <Form.Item
                                name="flight_op_id"
                                label={
                                    <Space>
                                        <span>系统内航班</span>
                                        <Tooltip title="从系统中的航班数据库选择现有航班">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ required: true, message: "请选择航班" }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="输入航班号搜索..."
                                    suffixIcon={<SearchOutlined />}
                                    filterOption={false}
                                    onSearch={handleFlightSearch}
                                    onChange={handleFormFieldChange}
                                    loading={searchLoading}
                                    optionLabelProp="label"
                                    notFoundContent={
                                        searchLoading ? (
                                            <div style={{ textAlign: 'center', padding: '12px' }}>
                                                <Spin size="small" />
                                                <div style={{ marginTop: '8px' }}>搜索中...</div>
                                            </div>
                                        ) : flightSearchKeyword && flightSearchKeyword.length >= 2 ? (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="未找到匹配的航班"
                                                style={{ padding: '12px' }}
                                            />
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '12px', color: '#999' }}>
                                                请输入至少2个字符搜索航班
                                            </div>
                                        )
                                    }
                                    dropdownStyle={{ 
                                        maxHeight: 400, 
                                        minWidth: 450,
                                        width: 'auto'
                                    }}
                                >
                                    {(flightOptions || []).map(flight => {
                                        const flightNumber = flight.flight_number || `ID-${flight.id}`;
                                        const route = `${flight.departure_airport} → ${flight.arrival_airport}`;
                                        // 增加日期显示
                                        const departureTime = flight.etd || flight.std;
                                        const dateStr = departureTime ? dayjs(departureTime).format('MM-DD') : '';
                                        const label = dateStr 
                                            ? `${flightNumber}/${dateStr} ${route}`
                                            : `${flightNumber} ${route}`;
                                        
                                        return (
                                            <Option 
                                                key={flight.id} 
                                                value={flight.id}
                                                label={label}
                                            >
                                                {renderFlightOption(flight)}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        )}

                        {flightInputMode === "manual" && (
                            <Form.Item
                                name="transport_identifier"
                                label={
                                    <Space>
                                        <span>外部航班号/运输标识</span>
                                        <Tooltip title="手动输入外部航班号、卡车编号等运输工具标识">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ required: true, message: "请输入航班号或运输标识" }]}
                            >
                                <Input 
                                    placeholder="如：CA1234、TRUCK-001等" 
                                    onChange={handleFormFieldChange}
                                />
                            </Form.Item>
                        )}
                    </>
                );

            case "OTHER_LOCATION":
                return (
                    <Form.Item
                        name="new_other_location"
                        label="其他位置描述"
                        rules={[{ required: true, message: "请输入位置描述" }]}
                    >
                        <Input.TextArea 
                            placeholder="请输入具体位置描述" 
                            rows={3}
                            maxLength={500}
                            onChange={handleFormFieldChange}
                        />
                    </Form.Item>
                );

            default:
                return null;
        }
    };

    // 【简化】一致性错误表格列定义 - 只显示ULD号和位置信息
    const errorColumns = [
        {
            title: 'ULD号',
            dataIndex: 'details',
            key: 'uld_identifier',
            width: 120,
            render: (details) => (
                <Text code strong>{details?.uld_identifier || '-'}</Text>
            )
        },
        {
            title: '当前位置/目标位置',
            dataIndex: 'details',
            key: 'location',
            render: (details) => {
                if (details?.check_type === 'ULD移入航班') {
                    // ULD移入航班：显示当前位置 → 期望位置
                    return (
                        <Space>
                            <Tag color="orange">{details?.current_airport || '-'}</Tag>
                            <span>→</span>
                            <Tag color="green">{details?.expected_airport || '-'}</Tag>
                        </Space>
                    );
                } else {
                    // ULD移出航班：显示目标位置 → 期望位置
                    return (
                        <Space>
                            <Tag color="orange">{details?.new_airport || '-'}</Tag>
                            <span>→</span>
                            <Tag color="green">{details?.expected_airport || '-'}</Tag>
                        </Space>
                    );
                }
            }
        }
    ];

    return (
        <Modal
            title={
                <span>
                    <EnvironmentOutlined style={{ marginRight: 8 }} />
                    批量更新ULD位置
                </span>
            }
            open={visible}
            onCancel={onCancel}
            footer={null} // 自定义底部按钮
            width={800}
        >
            {/* 【新增】前置检查结果展示 */}
            {showPreCheck && (
                <>
                    <Alert
                        type="warning"
                        message="部分ULD无法更新位置"
                        description={`检测到 ${preCheckResult.ineligible.length} 个ULD因状态限制无法更新位置，将被跳过。`}
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    
                    <Divider orientation="left">不符合条件的ULD</Divider>
                    <List
                        size="small"
                        dataSource={preCheckResult.ineligible}
                        renderItem={(item) => (
                            <List.Item>
                                <Space>
                                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                    <Text code>{String(item?.identifier || "未知")}</Text>
                                    <Tag color={ULD_CORE_STATUS_COLORS[item?.coreStatus] || 'default'}>
                                        {ULD_CORE_STATUS_NAMES[item?.coreStatus] || String(item?.coreStatus || '未知')}
                                    </Tag>
                                    <Text type="secondary">{String(item?.reason || "未知原因")}</Text>
                                </Space>
                            </List.Item>
                        )}
                        style={{ 
                            maxHeight: 150, 
                            overflow: 'auto',
                            marginBottom: 16,
                            border: '1px solid #f0f0f0',
                            padding: 8,
                            borderRadius: 6
                        }}
                    />
                </>
            )}

            {/* 符合条件的ULD数量提示 */}
            {preCheckResult.eligible.length > 0 && (
                <Alert
                    type="info"
                    message={`将更新 ${preCheckResult.eligible.length} 个ULD的位置信息`}
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    location_type: "AIRPORT"
                }}
            >
                {/* 【新增】位置一致性校验警告 */}
                {consistencyErrors.length > 0 && (
                    <Alert
                        message="位置一致性警告"
                        description={
                            <div>
                                <p>
                                    <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                    检测到 <Text strong style={{ color: '#ff4d4f' }}>{consistencyErrors.length}</Text> 个ULD存在位置一致性问题：
                                </p>
                                
                                <Table 
                                    dataSource={consistencyErrors}
                                    columns={errorColumns}
                                    size="small"
                                    pagination={false}
                                    rowKey={(record) => record.details?.uld_identifier || Math.random()}
                                    style={{ marginBottom: 16 }}
                                    scroll={{ y: 200 }}
                                />
                                
                                <div style={{ marginTop: 16 }}>
                                    <Text strong>您可以选择：</Text>
                                    <ul style={{ marginTop: 8, marginBottom: 8 }}>
                                        <li>取消操作，修改选择的{consistencyErrors[0]?.details?.check_type === "ULD移入航班" ? "航班" : "机场"}</li>
                                        <li>强制执行更新（如有特殊原因，请在备注中说明）</li>
                                        <li>
                                            <Button 
                                                type="link" 
                                                size="small"
                                                icon={<CopyOutlined />}
                                                onClick={() => copyToClipboard(consistencyErrors.map(item => item.details?.uld_identifier).filter(Boolean).join('\n'))}
                                            >
                                                复制问题ULD号
                                            </Button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item
                    name="location_type"
                    label="位置类型"
                    rules={[{ required: true, message: "请选择位置类型" }]}
                >
                    <Radio.Group onChange={handleLocationTypeChange}>
                        {locationTypeOptions.map(option => (
                            <Radio key={option.value} value={option.value}>
                                {option.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Form.Item>

                {renderLocationFields()}

                <Form.Item
                    name="reason"
                    label={consistencyErrors.length > 0 ? "变更原因（强制更新时必填）" : "变更原因"}
                    rules={consistencyErrors.length > 0 && forceUpdate ? [
                        { required: true, message: "强制更新时必须填写变更原因" }
                    ] : []}
                >
                    <Input.TextArea 
                        placeholder={consistencyErrors.length > 0 ? "请详细说明强制更新的原因..." : "请输入位置变更原因（可选）"} 
                        rows={consistencyErrors.length > 0 ? 3 : 2}
                        maxLength={500}
                    />
                </Form.Item>

                {/* 自定义底部按钮 */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: 16,
                    borderTop: '1px solid #f0f0f0',
                    marginTop: 16
                }}>
                    <div>
                        {consistencyErrors.length > 0 && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                ⚠️ 强制更新将跳过位置一致性检查，并在操作日志中标记
                            </Text>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button onClick={onCancel}>
                            取消操作
                        </Button>
                        {consistencyErrors.length > 0 ? (
                            <Button 
                                type="primary" 
                                danger
                                onClick={handleForceUpdate}
                                loading={loading}
                                disabled={preCheckResult.eligible.length === 0}
                            >
                                强制执行更新
                            </Button>
                        ) : (
                            <Button 
                                type="primary" 
                                onClick={handleSubmit}
                                loading={loading}
                                disabled={preCheckResult.eligible.length === 0}
                            >
                                确认更新
                            </Button>
                        )}
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default LocationBatchUpdateModal; 